/** @flow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Helmet from 'react-helmet';
import classnames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import Drawer from 'material-ui/Drawer';
import { Toolbar, ToolbarGroup } from 'material-ui/Toolbar';
import MenuIcon from 'material-ui/svg-icons/navigation/menu';
import { translate } from 'react-i18next';

import type { SnackbarState } from 'common/modules/ui/types/State';
import SplashScreen from 'common/modules/ui/components/SplashScreen';
import { head } from 'common/config/config';
import Snackbar from 'common/modules/ui/components/Snackbar';
import { closeSnackbar } from 'common/modules/ui/actions/uiActions';
import SessionManager from 'common/modules/session/SessionManager';
import ScrollToTop from 'common/layout/ScrollToTop';
import { Layout, Logo } from 'common/layout/styles';

import type AppState from '../redux/types/AppState';
import { closeSession, beginSession, renewSession } from '../modules/authentication/actions/sessionActions';
import Menu from './Menu';
import Routes from './Routes';
import { Images } from '../themes';
import ProfileDropDown from '../modules/userProfile/components/ProfileDropDown';

type Props = {
  snackbar: SnackbarState;
  isFetched: boolean;
  isAuthenticated: boolean;
  t: any,
  actions: {
    closeSession: () => mixed;
    closeSnackbar: () => mixed;
    beginSession: () => mixed,
    renewSession: () => mixed,
  };
}

type State = {
  open: boolean;
  forceMenuDisplay: boolean;
}

class App extends Component<Props, State> {

  constructor(props) {
    super(props);

    this.state = {
      open: false,
      forceMenuDisplay: true,
    };
  }

  componentWillMount() {
    this.handleWindowResize();
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const menuChanged = nextState.forceMenuDisplay !== this.state.forceMenuDisplay || nextState.open !== this.state.open;
    return this.props !== nextProps || menuChanged;
  }

  handleWindowResize = () => {
    if (window.innerWidth > 1380) {
      this.setState({ forceMenuDisplay: true, open: false });
    } else {
      this.setState({ forceMenuDisplay: false, open: false });
    }
  }

  logout = () => {
    this.setState({ open: false });
    this.props.actions.closeSession();
  }

  handleToggleDrawer = () => this.setState({ open: !this.state.open });

  isUnauthenticatedRoute = ({ pathname }) => {
    const unauthenticatedRoutes = [
      '/login',
      '/sign-up/with-*/*',
      '/access/with-*/*',
      '/accept-invitation',
      '/forgot-password',
      '/reset-password',
      '/proposition-printing',
      '/confirm-email',
      '/campaign-printing',
    ];
    return unauthenticatedRoutes.filter(r => pathname.match(r)).length !== 0;
  }

  render() {
    const { isAuthenticated, isFetched } = this.props;

    return (
      <Router>
        <ScrollToTop>
          <Route render={({ location, history }) => (
            <Layout style={{ height: 'auto' }}>
              <Helmet {...head} />
              {!this.isUnauthenticatedRoute(location) && isAuthenticated && isFetched &&
                <div>
                  <Drawer onRequestChange={this.handleToggleDrawer} docked={this.state.forceMenuDisplay} open={this.state.open || this.state.forceMenuDisplay} >
                    <Menu onLinkClick={this.handleToggleDrawer} />
                  </Drawer>
                  <Toolbar
                    className={classnames('app-bar', { extended: this.state.forceMenuDisplay })}
                    style={{
                      height: 64,
                      boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px',
                      zIndex: 1200,
                    }}
                  >
                    <ToolbarGroup>
                      {!this.state.forceMenuDisplay &&
                      <MenuIcon onClick={this.handleToggleDrawer} style={{ marginRight: 24, color: 'white' }} />
                    }
                      <Logo src={Images.logoWhite} alt="logo" onClick={() => history.push('/')} style={{ cursor: 'pointer', margin: 'auto' }} />
                    </ToolbarGroup>
                    <ToolbarGroup>
                      <ProfileDropDown />
                    </ToolbarGroup>
                  </Toolbar>
                </div>
              }
              <div className={classnames('app-content', {
                expanded: this.state.forceMenuDisplay && !this.isUnauthenticatedRoute(location),
                'with-app-bar': isAuthenticated && !this.isUnauthenticatedRoute(location),
              })}
              >
                <SessionManager
                  isAuthenticated={isAuthenticated}
                  beginSession={this.props.actions.beginSession}
                  renewSession={this.props.actions.renewSession}
                />
                <ReactCSSTransitionGroup
                  transitionName="fade"
                  transitionAppear
                  transitionAppearTimeout={500}
                  transitionEnterTimeout={10000}
                  transitionLeaveTimeout={10000}
                >
                  {isFetched ?
                    <Routes location={location} />
                    :
                    <SplashScreen />
                  }
                </ReactCSSTransitionGroup>
              </div>
              <Snackbar
                visible={this.props.snackbar.isShowing}
                message={this.props.snackbar.message}
                type={this.props.snackbar.kind}
                onClose={this.props.actions.closeSnackbar}
              />
            </Layout>
          )}
          />
        </ScrollToTop>
      </Router>
    );
  }
}

function mapStateToProps(state: AppState) {
  return {
    snackbar: state.ui.snackbar,
    isFetched: state.session.isFetched,
    isAuthenticated: state.session.isAuthenticated,
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({ closeSnackbar, closeSession, beginSession, renewSession }, dispatch),
  };
}

export default translate()(connect(mapStateToProps, mapDispatchToProps)(App));



// WEBPACK FOOTER //
// ./src/advertisers/layout/App.js